import React from 'react'
import { useTranslation } from 'react-i18next'
import { View, Text, StyleSheet } from 'react-native'
import { currencyMask } from '../../../../utils/helpers/masking'
import { IPaymentMethodData } from '../flows/PayAsGuestFlow/types'

type RowProps = {
  label: string
  value: string | number
  isBold?: boolean
  divider?: boolean
}

const Row: React.FC<RowProps> = ({
  label,
  value,
  isBold = false,
  divider = false,
}) => {
  return (
    <View style={[rowStyles.row, divider && rowStyles.rowWithDivider]}>
      <Text style={[rowStyles.rowLabel, isBold && rowStyles.boldValue]}>
        {label}
      </Text>
      <Text
        style={[
          rowStyles.rowLabel,
          rowStyles.rowValue,
          isBold && rowStyles.boldValue,
        ]}
      >
        {value}
      </Text>
    </View>
  )
}

type Props = {
  invoice: {
    company: {
      name: string
      phone: string
    }
    total_amount: number
    type: string
    invoice_number: string
    invoice_due_date: string
  }
  paymentMethodData: IPaymentMethodData
}

const PaymentInfo: React.FC<Props> = ({ invoice, paymentMethodData }) => {
  const { t } = useTranslation('global')

  const formatCardDetails = (_paymentMethod: Props['paymentMethodData']) => {
    const { card } = _paymentMethod

    const lastFourDigits = card?.last4 ?? '****'
    return t('integrations.pay-as-guest.payment-details.card-ending-in', {
      last4: lastFourDigits,
    })
  }

  const rows = [
    {
      label: t('integrations.pay-as-guest.payment-details.supplier'),
      value: invoice.company.name,
    },
    {
      label: t('integrations.pay-as-guest.payment-details.invoice-num'),
      value: '#' + invoice.invoice_number,
    },
    {
      label: t('integrations.pay-as-guest.payment-details.invoice-due-date'),
      value: invoice.invoice_due_date,
    },
    {
      label: t('integrations.pay-as-guest.payment-details.payment-method'),
      value: formatCardDetails(paymentMethodData),
      divider: true,
    },
    {
      label: t('integrations.invoice.invoice-amount'),
      value: currencyMask(invoice.total_amount),
    },
    {
      label: t('integrations.invoice.service-fee'),
      value: currencyMask(paymentMethodData.payment?.fee),
      divider: true,
    },
    {
      label: t('integrations.pay-as-guest.payment-details.total-paid'),
      value: currencyMask(
        Number(invoice.total_amount) + Number(paymentMethodData.payment?.fee),
      ),
      isBold: true,
    },
  ]

  return (
    <View style={styles.modalContainer}>
      {/* Title Section */}
      <View style={styles.headerContainer}>
        <Text style={styles.title}>
          {t('integrations.pay-as-guest.payment-details.title')}
        </Text>
      </View>

      {/* Table Section */}
      <View style={styles.tableContainer}>
        {rows.map((row, index) => (
          <Row
            key={index}
            label={row.label}
            value={row.value}
            isBold={row.isBold}
            divider={row.divider}
          />
        ))}
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  modalContainer: {
    width: '100%',
    flex: 1,
    padding: 20,
  },
  headerContainer: {
    marginBottom: 20,
  },
  title: {
    fontSize: 18,
    lineHeight: 32,
    fontFamily: 'Inter',
    fontWeight: '700',
    color: '#001929',
  },
  tableContainer: {
    marginBottom: 20,
  },
})

const rowStyles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
  },
  rowWithDivider: {
    borderBottomWidth: 1,
    borderBottomColor: '#EBEEEF',
    paddingBottom: 20,
    marginBottom: 10,
  },
  rowLabel: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 24,
    color: '#001929',
  },
  rowValue: {
    fontWeight: '500',
    maxWidth: '50%',
    textAlign: 'right',
  },
  boldValue: {
    fontWeight: '700',
    fontSize: 16,
  },
})

export default PaymentInfo
